import { Fragment, MouseEvent, ReactNode } from 'react';
import {
    CopyType,
    DentalExamination,
    DentalProcedure,
    Tool,
} from '../../../store//types/prescription';
import moment from 'moment';
import { getPrintableProperties } from '../../../components/prescriptionPad/rightPanel/workspace/MedCard';
import { GeniePadElementsSettingItem } from '../../../api/pushGenie';
import { PadElements } from '../../../components/prescriptionPad/SummaryView';
import {
    AdvicesIcon,
    canvasIcon,
    DentalExaminationIcon,
    DentalProcedureIcon,
    filesCopyIcons,
    followUpIcon,
    LabVital,
    PrescriptionNotesIcon,
    ProcedureIcon,
    ReferIcon,
} from '../../../components/common/Icons';
import { Actions } from '../../../store/actions';
import {
    copyCareCanvas,
    updateExaminationFindings,
    updateFollowup,
    updatePPNotes,
    updateReferal,
    upsertAdvice,
    upsertCurrentMedications,
    upsertDentalExamination,
    upsertDentalProcedure,
    upsertDiagnose,
    upsertElement,
    upsertInjection,
    upsertLabTest,
    upsertLabVital,
    upsertMedication,
    upsertSymptom,
} from '../../../store/actions/prescriptionPad';
import { computeFollowUp } from '../../BorrowPrescription';
import {
    DENTAL_EXAMINATIONS,
    DENTAL_PROCEDURES,
    DIAGNOSIS,
    INJECTIONS,
    INVESTIGATIONS,
    MEDICINES,
    PROCEDURES,
    SYMPTOMS,
} from '../../../strings';
import { convertToCurrentMedications, medWithQuantity, sanitizeMed } from '../../../store/utils';
import { MAGIC_BOX } from '../../../components/prescriptionPad/trackSource';
import omit from 'lodash/omit';
import groupBy from 'lodash/groupBy';
import { store } from '../../../App';
import { notification } from 'antd';
import { batch } from 'react-redux';
import uniq from 'lodash/uniq';
import { TEETH_TO_NAME } from '../../../components/prescriptionPad/leftPanel/DentalChart/constants';
import { rxKeyToHeadingMap, rxKeyToIconMap } from '../../opUtils';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalStateV1 } from '../../../store/reducers';
import { setCareCanvasPreview } from '../../../store/actions/session';

export type P = {
    visit: Tool & { date: string; visitId: string };
    padElementsConfig: GeniePadElementsSettingItem[];
};

const copyIcon = filesCopyIcons();

export const configs: {
    [key in keyof PadElements]: {
        createCopyAction: (
            visit: Tool & { date: string },
            id: keyof Tool,
            type?: CopyType,
        ) => Actions | undefined | Actions[];
        icon: {
            cssClass: string;
            text: JSX.Element | string;
            tooltipContent: (visit: Tool, id: keyof Tool) => ReactNode;
        };
        content: (
            visit: Tool,
            id: keyof Tool,
            forPtOverview?: boolean,
            dispatch?: ThunkDispatch<GlobalStateV1, null, Actions>,
        ) => ReactNode;
    };
} = {
    refer: {
        createCopyAction: (visit) => updateReferal(visit.refer),
        icon: {
            cssClass: 'bg-darwin-accent-medical-history-orange-200',
            text: ReferIcon(''),
            tooltipContent: () => {
                return 'Refer';
            },
        },
        content: (visit: Tool) => {
            if (!visit.refer) {
                return null;
            }
            return (
                <p className="">
                    Reffered to
                    {visit.refer.doc && visit.refer.doc.length > 0 ? (
                        visit.refer.doc.map((item) => (
                            <p key={item.name?.f}>
                                &#8226;{' '}
                                {item.type === 'speciality'
                                    ? item.s_name
                                    : `Dr. ${[item.name?.f, item.name?.l]
                                          .filter(Boolean)
                                          .join(' ')}`}{' '}
                                {item.phone?.value && '|'}{' '}
                                {item.phone?.value
                                    ? [item.phone?.code, item.phone?.value]
                                          .filter(Boolean)
                                          .join(' ')
                                    : null}
                            </p>
                        ))
                    ) : (
                        <p className="w-full text-neutral-3 text-13">[EKA]</p>
                    )}
                </p>
            );
        },
    },
    prescriptionNotes: {
        createCopyAction: (visit) =>
            visit.prescriptionNotes && updatePPNotes(visit.prescriptionNotes),
        icon: {
            cssClass: 'bg-darwin-accent-green-200',
            text: PrescriptionNotesIcon(''),
            tooltipContent: () => {
                return 'Notes';
            },
        },
        content: (visit: Tool) => {
            if (!visit.prescriptionNotes) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <div style={{ whiteSpace: 'break-spaces' }} className="space-y-10">
                        <div
                            className="flex flex-col tiny-mce"
                            dangerouslySetInnerHTML={{
                                __html:
                                    visit?.prescriptionNotes?.text ||
                                    visit?.prescriptionNotes?.parsedText,
                            }}
                        />
                    </div>
                </p>
            );
        },
    },
    advices: {
        createCopyAction: (visit) => visit.advices?.map((a) => upsertAdvice(a)),
        icon: {
            tooltipContent: () => {
                return 'Advices';
            },
            cssClass: 'bg-darwin-accent-mauve-200',
            text: AdvicesIcon('fill-darwin-accent-mauve-900'),
        },
        content: (visit: Tool) => {
            if (!visit.advices?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <div className="space-y-10">
                        {visit.advices.map((a) => (
                            <p key={a.id} className="text-neutral-black">
                                <div
                                    className="flex flex-col tiny-mce"
                                    dangerouslySetInnerHTML={{
                                        __html: a?.text || a?.parsedText,
                                    }}
                                />
                            </p>
                        ))}
                    </div>
                </p>
            );
        },
    },
    followup: {
        createCopyAction: (visit) => {
            const t = computeFollowUp(visit.date, visit.followup);
            return t && updateFollowup(t.date, undefined, t.notes);
        },
        icon: {
            cssClass: 'bg-darwin-neutral-800',
            text: followUpIcon('fill-white'),
            tooltipContent: () => {
                return 'Follow Up';
            },
        },
        content: (visit: Tool) => {
            if (!(visit.followup?.date || visit.followup?.notes)) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <p className="text-neutral-black">
                        {visit.followup.date
                            ? moment(visit.followup.date).format('Do MMM YYYY')
                            : ''}{' '}
                    </p>
                    <p style={{ whiteSpace: 'break-spaces' }}>{visit.followup.notes}</p>
                </p>
            );
        },
    },
    diagnosis: {
        createCopyAction: (visit) => visit.diagnosis?.map((a) => upsertDiagnose(a)),
        icon: {
            cssClass:
                'bg-darwin-accent-diagnosis-purple-200 text-darwin-accent-diagnosis-purple-800',
            text: 'Dx',
            tooltipContent: () => {
                return DIAGNOSIS;
            },
        },
        content: (visit: Tool) => {
            if (!visit.diagnosis?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    {visit.diagnosis
                        ?.map((t) => {
                            return `${t?.name}${t?.icd10_code ? ` - ${t?.icd10_code}` : ''}`;
                        })
                        .join(' | ')}
                </p>
            );
        },
    },
    injections: {
        createCopyAction: (visit, id, type) => {
            if (type === CopyType.CurrentMX) {
                const currMx = (visit?.injections || []).map((el) =>
                    convertToCurrentMedications(el, visit?.date),
                );
                return upsertCurrentMedications(currMx);
            }
            return visit.injections?.map((injection) => upsertInjection(injection));
        },
        icon: {
            cssClass: 'bg-darwin-accent-medicines-pink-200 text-darwin-accent-medicines-pink-800',
            text: 'Inj',
            tooltipContent: () => {
                return INJECTIONS;
            },
        },
        content: (visit: Tool) => {
            if (!visit.injections?.length) {
                return null;
            }
            return visit.injections?.map((injection) => (
                <div key={injection.id}>
                    <p className="text-darwin-neutral-1000 font-400 text-12">
                        <span className="uppercase font-700">{injection.name}</span>{' '}
                        {injection.generic_name && `(${injection.generic_name})`}
                    </p>
                    {injection.added_drug?.map((drug) => (
                        <p className="text-darwin-neutral-1000 font-400 text-12">
                            <span className="uppercase font-700">{drug.name}</span>{' '}
                            {drug.generic_name && `(${drug.generic_name})`}
                        </p>
                    ))}
                </div>
            ));
        },
    },
    symptoms: {
        createCopyAction: (visit) => visit.symptoms?.map((a) => upsertSymptom(a)),
        icon: {
            cssClass: 'bg-darwin-accent-symptoms-blue-200 text-darwin-accent-symptoms-blue-800',
            text: 'Sx',
            tooltipContent: () => {
                return SYMPTOMS;
            },
        },
        content: (visit: Tool) => {
            if (!visit.symptoms?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    {visit.symptoms?.map((t) => t.name).join(' | ')}
                </p>
            );
        },
    },
    opVision: {
        createCopyAction: (visit) => visit.opVision?.map((a) => upsertElement('opVision', a)),
        icon: {
            cssClass: 'bg-accent-dark-green text-white',
            text: rxKeyToIconMap['opVision'],
            tooltipContent: () => {
                return rxKeyToHeadingMap['opVision'];
            },
        },
        content: (visit: Tool) => {
            if (!visit.opVision?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <ul>
                        {visit.opVision.map((i, index) =>
                            i.eye?.value ? (
                                <li>
                                    <b>{i.eye?.value}</b>:{' '}
                                    <span>
                                        {Object.entries(i)
                                            .filter(([k, v]) => k !== 'id' && k !== 'eye' && k !== 'name' && v)
                                            .map(([k, v]) => `${k.toUpperCase()}: ${v.value} `)}
                                    </span>
                                </li>
                            ) : null,
                        )}
                    </ul>
                </p>
            );
        },
    },
    opIop: {
        createCopyAction: (visit) => visit.opIop?.map((a) => upsertElement('opIop', a)),
        icon: {
            cssClass: 'bg-accent-dark-green text-white',
            text: rxKeyToIconMap['opIop'],
            tooltipContent: () => {
                return rxKeyToHeadingMap['opIop'];
            },
        },
        content: (visit: Tool) => {
            if (!visit.opIop?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <ul>
                        {visit.opIop.map((i, index) =>
                            i.eye?.value ? (
                                <li>
                                    <b>{i.eye?.value}</b>:{' '}
                                    <span>
                                        {Object.entries(i)
                                            .filter(([k, v]) => k !== 'id' && k !== 'eye' && k !== 'name' && v)
                                            .map(([k, v]) => `${k.toUpperCase()}: ${v.value} `)}
                                    </span>
                                </li>
                            ) : null,
                        )}
                    </ul>
                </p>
            );
        },
    },
    opCurrentSpec: {
        createCopyAction: (visit) =>
            visit.opCurrentSpec?.map((a) => upsertElement('opCurrentSpec', a)),
        icon: {
            cssClass: 'bg-accent-dark-green text-white',
            text: rxKeyToIconMap['opCurrentSpec'],
            tooltipContent: () => {
                return rxKeyToHeadingMap['opCurrentSpec'];
            },
        },
        content: (visit: Tool) => {
            if (!visit.opCurrentSpec?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <ul>
                        {visit.opCurrentSpec.map((i, index) =>
                            i.eye?.value ? (
                                <li>
                                    <b>{i.eye?.value}</b>:{' '}
                                    <span>
                                        {Object.entries(i)
                                            .filter(([k, v]) => k !== 'id' && k !== 'eye' && k !== 'name' && v)
                                            .map(([k, v]) => `${k.toUpperCase()}: ${v.value} `)}
                                    </span>
                                </li>
                            ) : null,
                        )}
                    </ul>
                </p>
            );
        },
    },
    opSubjectiveRefraction: {
        createCopyAction: (visit) =>
            visit.opSubjectiveRefraction?.map((a) => upsertElement('opSubjectiveRefraction', a)),
        icon: {
            cssClass: 'bg-accent-dark-green text-white',
            text: rxKeyToIconMap['opSubjectiveRefraction'],
            tooltipContent: () => {
                return rxKeyToHeadingMap['opSubjectiveRefraction'];
            },
        },
        content: (visit: Tool) => {
            if (!visit.opSubjectiveRefraction?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <ul>
                        {visit.opSubjectiveRefraction.map((i, index) =>
                            i.eye?.value ? (
                                <li>
                                    <b>{i.eye?.value}</b>:{' '}
                                    <span>
                                        {Object.entries(i)
                                            .filter(([k, v]) => k !== 'id' && k !== 'eye' && k !== 'name' && v)
                                            .map(([k, v]) => `${k.toUpperCase()}: ${v.value} `)}
                                    </span>
                                </li>
                            ) : null,
                        )}
                    </ul>
                </p>
            );
        },
    },
    opAutoRefraction: {
        createCopyAction: (visit) =>
            visit.opAutoRefraction?.map((a) => upsertElement('opAutoRefraction', a)),
        icon: {
            cssClass: 'bg-accent-dark-green text-white',
            text: rxKeyToIconMap['opAutoRefraction'],
            tooltipContent: () => {
                return rxKeyToHeadingMap['opAutoRefraction'];
            },
        },
        content: (visit: Tool) => {
            if (!visit.opAutoRefraction?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <ul>
                        {visit.opAutoRefraction.map((i, index) =>
                            i.eye?.value ? (
                                <li>
                                    <b>{i.eye?.value}</b>:{' '}
                                    <span>
                                        {Object.entries(i)
                                            .filter(([k, v]) => k !== 'id' && k !== 'eye' && k !== 'name' && v)
                                            .map(([k, v]) => `${k.toUpperCase()}: ${v.value} `)}
                                    </span>
                                </li>
                            ) : null,
                        )}
                    </ul>
                </p>
            );
        },
    },
    opFinalPrescription: {
        createCopyAction: (visit) =>
            visit.opFinalPrescription?.map((a) => upsertElement('opFinalPrescription', a)),
        icon: {
            cssClass: 'bg-accent-dark-green text-white',
            text: rxKeyToIconMap['opFinalPrescription'],
            tooltipContent: () => {
                return rxKeyToHeadingMap['opFinalPrescription'];
            },
        },
        content: (visit: Tool) => {
            if (!visit.opFinalPrescription?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    <ul>
                        {visit.opFinalPrescription.map((i, index) =>
                            i.eye?.value ? (
                                <li>
                                    <b>{i.eye?.value}</b>:{' '}
                                    <span>
                                        {Object.entries(i)
                                            .filter(([k, v]) => k !== 'id' && k !== 'eye'&& k !== 'name' && v)
                                            .map(([k, v]) => `${k.toUpperCase()}: ${v.value} `)}
                                    </span>
                                </li>
                            ) : null,
                        )}
                    </ul>
                </p>
            );
        },
    },
    labTests: {
        createCopyAction: (visit) => {
            return visit.labTests?.map((a, index) => {
                const isLastLabTest = visit?.labTests?.length === index + 1;
                return upsertLabTest(omit(a, ['test_on', 'repeat_on']), undefined, isLastLabTest);
            });
        },
        icon: {
            cssClass:
                'bg-darwin-accent-investigations-ochre-200 text-darwin-accent-investigations-ochre-800',
            text: 'Lab',
            tooltipContent: () => {
                return INVESTIGATIONS;
            },
        },
        content: (visit: Tool) => {
            if (!visit.labTests?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    {visit.labTests?.map((t) => t.name).join(' | ')}
                </p>
            );
        },
    },
    examinations: {
        createCopyAction: (visit) => visit.examinations?.map((a) => updateExaminationFindings(a)),
        icon: {
            cssClass: 'bg-darwin-accent-examination-findings-sky-blue-200',
            text: (
                <svg
                    role="img"
                    viewBox="0 0 13 13"
                    className="w-14 h-12 fill-darwin-accent-examination-findings-sky-blue-900"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M10.5 3.125C9.44531 3.125 8.625 3.96875 8.625 5C8.625 5.77344 9.07031 6.42969 9.75 6.73438V8.58594C9.75 9.92188 8.55469 11.0234 7.125 11.0234C5.71875 11.0234 4.54688 9.96875 4.5 8.67969C6.1875 8.32812 7.5 6.82812 7.5 5V1.36719C7.5 1.10938 7.28906 0.875 7.03125 0.828125L5.55469 0.523438C5.25 0.453125 4.96875 0.664062 4.89844 0.96875L4.82812 1.32031C4.75781 1.625 4.96875 1.92969 5.27344 2L5.97656 2.14062V4.97656C5.97656 6.21875 4.99219 7.25 3.75 7.25C2.50781 7.27344 1.47656 6.26562 1.47656 5V2.14062L2.20312 2C2.50781 1.92969 2.71875 1.64844 2.64844 1.34375L2.57812 0.96875C2.50781 0.664062 2.22656 0.453125 1.92188 0.523438L0.445312 0.804688C0.1875 0.875 0 1.10938 0 1.36719V5C0 6.82812 1.28906 8.32812 3 8.67969C3.04688 10.8125 4.875 12.5 7.125 12.5C9.39844 12.5 11.25 10.7422 11.25 8.5625V6.73438C11.9062 6.42969 12.375 5.77344 12.375 5.02344C12.375 3.96875 11.5312 3.125 10.5 3.125ZM10.5 5.5625C10.1719 5.5625 9.9375 5.32812 9.9375 5C9.9375 4.69531 10.1719 4.4375 10.5 4.4375C10.8047 4.4375 11.0625 4.69531 11.0625 5C11.0625 5.32812 10.8047 5.5625 10.5 5.5625Z" />
                </svg>
            ),
            tooltipContent: () => {
                return 'Examinations';
            },
        },
        content: (visit: Tool, _, forPtOverview?: boolean) => {
            if (!visit.examinations?.length) {
                return null;
            }
            return forPtOverview ? (
                <div className="text-darwin-neutral-900 text-13 space-y-4">
                    {visit.examinations?.map((t) => (
                        <p>
                            <span className="font-700">{t.name}: </span>
                            {t.notes}
                        </p>
                    ))}
                </div>
            ) : (
                <p className="text-darwin-neutral-900 text-13">
                    {visit.examinations?.map((t) => t.name).join(' | ')}
                </p>
            );
        },
    },
    labVitals: {
        createCopyAction: (visit) => visit.labVitals?.map((a) => upsertLabVital(a)),
        icon: {
            cssClass: 'bg-darwin-accent-investigations-ochre-100',
            text: LabVital('fill-darwin-accent-bright-yellow-900'),
            tooltipContent: () => {
                return 'Lab Results';
            },
        },
        content: (visit: Tool) => {
            if (!visit.labVitals || !visit.labVitals?.length) {
                return null;
            }
            return (
                <p className="text-darwin-neutral-900 text-13">
                    {visit.labVitals?.map((l, index) => {
                        const text = [l.value, l.unit?.name, l.interpretation?.value || '-']
                            .filter(Boolean)
                            .join(' ');
                        return (
                            <Fragment>
                                <span key={l.id}>
                                    <span className="">{l.name}</span>
                                    <span className="font-700">{text ? `: ${text}` : ''}</span>{' '}
                                </span>
                                {visit.labVitals && index < visit.labVitals.length - 1 && (
                                    <span className=""> | </span>
                                )}
                            </Fragment>
                        );
                    })}
                </p>
            );
        },
    },
    medications: {
        createCopyAction: (visit, key, type) => {
            if (!visit.medications?.length) {
                return;
            }
            if (type === CopyType.CurrentMX) {
                const currMx = (visit?.medications || []).map((el) =>
                    convertToCurrentMedications(el, visit?.date),
                );
                return upsertCurrentMedications(currMx);
            } else {
                return upsertMedication(
                    visit.medications.map((a) => ({
                        ...medWithQuantity(sanitizeMed(a)),
                        track: { source: MAGIC_BOX },
                    })),
                );
            }
        },
        icon: {
            cssClass: 'bg-darwin-accent-medicines-pink-200 text-darwin-accent-medicines-pink-800',
            text: 'Mx',
            tooltipContent: () => {
                return MEDICINES;
            },
        },
        content: (visit: Tool) => {
            if (!visit.medications?.length) {
                return null;
            }
            return visit.medications?.map((t) => (
                <div key={t.id}>
                    <p className="text-darwin-neutral-1000 font-400 text-12">
                        <span className="uppercase font-700">{t.name}</span>{' '}
                        {t.generic_name && `(${t.generic_name})`}
                    </p>
                    {[t, ...(t.tapering_dose || [])].map((d) => (
                        <div className="flex space-x-6 mt-2">
                            {d.start_from && (
                                <div>
                                    <p className="text-10 uppercase border-b border-l p-3 min-w-68">
                                        {d.start_from.text}:
                                    </p>
                                </div>
                            )}
                            <p className="text-darwin-neutral-700 text-13">
                                {getPrintableProperties(d)
                                    .filter((item) => item.what !== 'start_from')
                                    .map((item) => item.value)
                                    .join(' | ')}
                            </p>
                        </div>
                    ))}
                </div>
            ));
        },
    },
    dentalExaminations: {
        createCopyAction: (visit) =>
            visit.dentalExaminations?.map((a) => upsertDentalExamination(a)),
        icon: {
            cssClass: 'bg-darwin-green-bg-dark',
            text: DentalExaminationIcon('w-12 h-12 fill-darwin-green-primary'),
            tooltipContent: () => {
                return DENTAL_EXAMINATIONS;
            },
        },
        content: (visit: Tool) => {
            if (!visit.dentalExaminations?.length) {
                return null;
            }
            function onCopy(
                e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>,
                t: DentalExamination[],
            ) {
                e.stopPropagation();
                batch(() => {
                    t.forEach((t) => {
                        store.dispatch(upsertDentalExamination(t));
                    });
                });
                notification.success({
                    message: `Copied to Pad`,
                    duration: 5,
                    placement: 'bottomRight',
                });
            }
            const groupByTooth = groupBy(visit.dentalExaminations, (i) => i.group_id || i.teeth_id);
            return Object.entries(groupByTooth)?.map((t, index) => {
                const teeth = uniq(
                    t[1].map((i) => TEETH_TO_NAME[i.teeth_id || ''] || `T${i.teeth_id}`),
                ).join(', ');
                const examinations = uniq(t[1].map((i) => i.name)).join(', ');
                return (
                    <div key={teeth}>
                        {index === 0 && (
                            <p className="text-darwin-neutral-1000 font-700 pt-2 pb-6">
                                Dental Examinations / Past Procedures
                            </p>
                        )}
                        <p className="hover-parent-element relative font-400 text-12 space-x-5 pr-24">
                            <span className="text-darwin-neutral-1000 uppercase font-700">
                                {teeth}
                            </span>
                            <span>{examinations}</span>
                            <span
                                onClick={(e) => onCopy(e, t[1])}
                                className="hover-child-element absolute top-0 right-0 h-18 w-18 flex justify-center items-center cursor-pointer p-3"
                            >
                                {copyIcon}
                            </span>
                        </p>
                    </div>
                );
            });
        },
    },
    dentalProcedures: {
        createCopyAction: (visit) => {
            const { dentalProcedures = [] } = visit;
            dentalProcedures.forEach((i) => delete i.service_id);
            return dentalProcedures?.map((a) => upsertDentalProcedure(a));
        },
        icon: {
            cssClass: 'bg-darwin-accent-medicines-pink-200',
            text: DentalProcedureIcon('w-14 h-12 fill-darwin-accent-medicines-pink-800'),
            tooltipContent: () => {
                return DENTAL_PROCEDURES;
            },
        },
        content: (visit: Tool) => {
            if (!visit.dentalProcedures?.length) {
                return null;
            }
            function onCopy(
                e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>,
                t: DentalProcedure,
            ) {
                e.stopPropagation();
                delete t.service_id;
                store.dispatch(upsertDentalProcedure(t));
                notification.success({
                    message: `Copied to Pad`,
                    duration: 5,
                    placement: 'bottomRight',
                });
            }
            return visit.dentalProcedures?.map((t, index) => (
                <div key={t.id}>
                    {index === 0 && (
                        <p className="text-darwin-neutral-1000 font-700 pt-2 pb-6">
                            Dental Procedures
                        </p>
                    )}
                    <p className="hover-parent-element relative font-400 text-12 space-x-5 pr-24">
                        <span className="text-darwin-neutral-1000 uppercase font-700">
                            {t.name}
                        </span>
                        <span>{t.teeth_ids?.map((i) => `T${i}`)?.join(', ')}</span>
                        <span
                            onClick={(e) => onCopy(e, t)}
                            className="hover-child-element absolute top-0 right-0 h-18 w-18 flex justify-center items-center cursor-pointer p-3"
                        >
                            {copyIcon}
                        </span>
                    </p>
                </div>
            ));
        },
    },
    procedures: {
        createCopyAction: (visit) => {
            const { procedures = [] } = visit;
            return procedures?.map((a) => upsertElement('procedures', a, undefined, true));
        },
        icon: {
            cssClass: 'bg-darwin-accent-medicines-pink-200',
            text: ProcedureIcon('w-14 h-12 fill-darwin-accent-medicines-pink-800'),
            tooltipContent: () => {
                return PROCEDURES;
            },
        },
        content: (visit: Tool) => {
            if (!visit.procedures?.length) {
                return null;
            }
            function onCopy(
                e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>,
                t: DentalProcedure,
            ) {
                e.stopPropagation();
                store.dispatch(upsertElement('procedures', t, undefined, true));
                notification.success({
                    message: `Copied to Pad`,
                    duration: 5,
                    placement: 'bottomRight',
                });
            }
            return visit.procedures?.map((t, index) => (
                <div key={t.id}>
                    {index === 0 && (
                        <p className="text-darwin-neutral-1000 font-700 pt-2 pb-6">Procedures</p>
                    )}
                    <p className="hover-parent-element relative font-400 text-12 space-x-5 pr-24">
                        <span className="text-darwin-neutral-1000">
                            <span className="uppercase font-700">{t.name}</span>{' '}
                            <span>{t.date && `(Date: ${moment(t.date).format('Do MMM YY')})`}</span>
                        </span>
                        <span
                            onClick={(e) => onCopy(e, t)}
                            className="hover-child-element absolute top-0 right-0 h-18 w-18 flex justify-center items-center cursor-pointer p-3"
                        >
                            {copyIcon}
                        </span>
                    </p>
                </div>
            ));
        },
    },
    careCanvas: {
        createCopyAction: (visit) => copyCareCanvas(visit.careCanvas),
        icon: {
            cssClass: 'bg-darwin-accent-mauve-200',
            text: canvasIcon('w-14 h-13 fill-darwin-accent-mauve-800 transform scale-90'),
            tooltipContent: () => {
                return 'Canvas';
            },
        },
        content: (visit: Tool, _, __, dispatch?: ThunkDispatch<GlobalStateV1, null, Actions>) => {
            const careCanvas = Array.isArray(visit?.careCanvas) ? visit?.careCanvas : [];
            if (!careCanvas?.length) return null;
            return (
                <div className="space-y-3">
                    {careCanvas.map((i) => {
                        const { name, final_image } = i;
                        return (
                            <p>
                                <span className="text-neutral-black">{name}</span>{' '}
                                <span className="text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700">
                                    <a
                                        href={final_image}
                                        target="_blank"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (dispatch) {
                                                e.preventDefault();
                                                dispatch(setCareCanvasPreview(i));
                                            }
                                        }}
                                    >
                                        (view)
                                    </a>
                                </span>
                            </p>
                        );
                    })}
                </div>
            );
        },
    },
};
